import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Calendar, CalendarClock, Clock, Users, Star, BarChart3, TrendingUp,
  CheckCircle2, Radio, Plus, Eye, ChevronRight, Video, Activity, Compass,
} from 'lucide-react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import StatCard from '../../components/ui/StatCard';
import PlanExpiredBanner from '../../components/billing/PlanExpiredBanner';
import { trainerService } from '../../services';
import { usePreferences } from '../../contexts/PreferencesContext';
import { getActivityIcon, getActivityTypeColor } from '../../utils/activityIcons';

// Shared chrome — matches ClientDashboard byte-for-byte.
const CARD_BASE =
  'rounded-2xl bg-white dark:bg-neutral-900 ring-1 ring-neutral-200/70 dark:ring-neutral-800 shadow-[0_1px_2px_rgba(16,24,40,0.04),0_8px_24px_-12px_rgba(16,24,40,0.08)]';

const STAT_CHIP = {
  violet:  'bg-violet-50 text-violet-600 ring-1 ring-inset ring-violet-100 dark:bg-violet-500/10 dark:text-violet-400 dark:ring-violet-500/20',
  blue:    'bg-blue-50 text-blue-600 ring-1 ring-inset ring-blue-100 dark:bg-blue-500/10 dark:text-blue-400 dark:ring-blue-500/20',
  emerald: 'bg-emerald-50 text-emerald-600 ring-1 ring-inset ring-emerald-100 dark:bg-emerald-500/10 dark:text-emerald-400 dark:ring-emerald-500/20',
  amber:   'bg-amber-50 text-amber-600 ring-1 ring-inset ring-amber-100 dark:bg-amber-500/10 dark:text-amber-400 dark:ring-amber-500/20',
};

const SECTION_HEADER_CHIP =
  'w-9 h-9 rounded-xl flex items-center justify-center bg-neutral-100 dark:bg-neutral-800 ring-1 ring-inset ring-neutral-200/70 dark:ring-neutral-800 text-neutral-600 dark:text-neutral-400 flex-shrink-0';

/**
 * Trainer Dashboard
 * Main overview page for trainers showing sessions, performance, and quick actions
 */

const TrainerDashboard = () => {
  const navigate = useNavigate();
  const { formatDateWithTimezone, formatTime } = usePreferences();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [upcomingSessionsList, setUpcomingSessionsList] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  // Getting-started checklist persistence (dismissal + action-based steps).
  const [gsDismissed, setGsDismissed] = useState(() => localStorage.getItem('trainer_gs_dismissed') === '1');
  const [viewedReport, setViewedReport] = useState(() => localStorage.getItem('trainer_gs_viewed_report') === '1');
  const [activitiesDone, setActivitiesDone] = useState(() => localStorage.getItem('trainer_gs_activities') === '1');

  // Check if individual/solo trainer
  const isSoloTrainer = () => {
    const org = user?.primaryOrganization || user?.organization;
    return org?.type === 'individual';
  };

  // Onboarding steps - different for solo vs normal trainers
  const soloTrainerSteps = [
    {
      icon: (
        <svg className="w-10 h-10" viewBox="0 0 24 24" fill="currentColor">
          <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
        </svg>
      ),
      title: 'Create Your Session',
      description: 'Go to My Sessions and click "Create Session". Set the session name, date, time, and expected participants to get started.',
      color: 'bg-blue-500',
    },
    {
      icon: (
        <svg className="w-10 h-10" viewBox="0 0 24 24" fill="currentColor">
          <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14zM7 10h2v7H7zm4-3h2v10h-2zm4 6h2v4h-2z"/>
        </svg>
      ),
      title: 'Setup Sub-Sessions & Activities',
      description: 'Open your session and add sub-sessions (topics). Then add activities like challenges, action plans, polls, and feedback forms to each sub-session.',
      color: 'bg-purple-500',
    },
    {
      icon: (
        <svg className="w-10 h-10" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 14.5v-9l6 4.5-6 4.5z"/>
        </svg>
      ),
      title: 'Start & Run Sessions',
      description: 'Start the session at scheduled time, share the session code or QR with participants, and launch activities during the training.',
      color: 'bg-green-500',
    },
    {
      icon: (
        <svg className="w-10 h-10" viewBox="0 0 24 24" fill="currentColor">
          <path d="M16 6l2.29 2.29-4.88 4.88-4-4L2 16.59 3.41 18l6-6 4 4 6.3-6.29L22 12V6h-6z"/>
        </svg>
      ),
      title: 'Monitor Engagement',
      description: 'Track real-time participant engagement, response rates, and activity completion during live sessions.',
      color: 'bg-amber-500',
    },
    {
      icon: (
        <svg className="w-10 h-10" viewBox="0 0 24 24" fill="currentColor">
          <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
        </svg>
      ),
      title: 'Review Reports & Feedback',
      description: 'After each session, review detailed reports, participant feedback, and your performance metrics to improve future sessions.',
      color: 'bg-red-500',
    },
  ];

  const normalTrainerSteps = [
    {
      icon: (
        <svg className="w-10 h-10" viewBox="0 0 24 24" fill="currentColor">
          <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14zM7 10h2v7H7zm4-3h2v10h-2zm4 6h2v4h-2z"/>
        </svg>
      ),
      title: 'Setup Your Session',
      description: 'Your client will create sessions and assign them to you. Open the assigned session and create sub-sessions (topics) with activities like challenges, action plans, polls, and feedback forms.',
      color: 'bg-blue-500',
    },
    {
      icon: (
        <svg className="w-10 h-10" viewBox="0 0 24 24" fill="currentColor">
          <path d="M19 3h-4.18C14.4 1.84 13.3 1 12 1c-1.3 0-2.4.84-2.82 2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 0c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm-2 14l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z"/>
        </svg>
      ),
      title: 'Review Participant Responses',
      description: 'Check pre-session challenges and personalization responses submitted by participants to tailor your training content.',
      color: 'bg-purple-500',
    },
    {
      icon: (
        <svg className="w-10 h-10" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 14.5v-9l6 4.5-6 4.5z"/>
        </svg>
      ),
      title: 'Start & Run Sessions',
      description: 'Start the session at scheduled time, share the session code with participants, and launch activities during the training.',
      color: 'bg-green-500',
    },
    {
      icon: (
        <svg className="w-10 h-10" viewBox="0 0 24 24" fill="currentColor">
          <path d="M16 6l2.29 2.29-4.88 4.88-4-4L2 16.59 3.41 18l6-6 4 4 6.3-6.29L22 12V6h-6z"/>
        </svg>
      ),
      title: 'Monitor Engagement',
      description: 'Track real-time participant engagement, response rates, and activity completion during live sessions.',
      color: 'bg-amber-500',
    },
    {
      icon: (
        <svg className="w-10 h-10" viewBox="0 0 24 24" fill="currentColor">
          <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
        </svg>
      ),
      title: 'Review Reports & Feedback',
      description: 'After each session, review detailed reports, participant feedback, and your performance metrics to improve.',
      color: 'bg-red-500',
    },
  ];

  const onboardingSteps = isSoloTrainer() ? soloTrainerSteps : normalTrainerSteps;

  useEffect(() => {
    // Get user from localStorage
    const userData = localStorage.getItem('user');
    if (userData) {
      const parsedUser = JSON.parse(userData);
      setUser(parsedUser);

      // Show onboarding only on the user's very first login (tracked in DB)
      if (parsedUser.isFirstLogin) {
        setShowOnboarding(true);
      }
    }

    // Fetch dashboard data from API
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        const response = await trainerService.getDashboardStats();
        if (response.success) {
          setDashboardData(response.data);
          setUpcomingSessionsList(response.data.upcomingSessions || []);
          setRecentActivity(response.data.recentActivity || []);
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  // Handle onboarding completion
  const handleCompleteOnboarding = (redirectToHelp = false) => {
    // Clear isFirstLogin flag from stored user data so it doesn't show again
    const userData = localStorage.getItem('user');
    if (userData) {
      const parsedUser = JSON.parse(userData);
      parsedUser.isFirstLogin = false;
      localStorage.setItem('user', JSON.stringify(parsedUser));
    }
    setShowOnboarding(false);
    setCurrentStep(0);

    // Redirect to help page if completing the full tour
    if (redirectToHelp) {
      navigate('/trainer/help');
    }
  };

  // Handle next step
  const handleNextStep = () => {
    if (currentStep < onboardingSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      // Last step - redirect to help page
      handleCompleteOnboarding(true);
    }
  };

  // Handle previous step
  const handlePrevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Re-open the welcome tour on demand (the "Show me around" button).
  const handleReplayTour = () => {
    setCurrentStep(0);
    setShowOnboarding(true);
  };

  // Permanently hide the getting-started checklist for this browser.
  const dismissGettingStarted = () => {
    localStorage.setItem('trainer_gs_dismissed', '1');
    setGsDismissed(true);
  };

  // Get stats from API response
  const stats = dashboardData?.stats || {};
  const activeSession = dashboardData?.activeSession;
  const sessionsToday = stats.todaySessions || 0;
  const upcomingSessions = stats.upcomingSessionsCount || 0;
  const totalParticipants = stats.totalParticipants || 0;
  const avgRating = stats.avgRating || 0;
  const planStatus = dashboardData?.planStatus || null;

  const statsCards = [
    { title: 'Sessions Today',     value: sessionsToday,         Icon: Calendar, variant: 'blue' },
    { title: 'Upcoming Sessions',  value: upcomingSessions,      Icon: Clock,    variant: 'violet' },
    { title: 'Total Participants', value: totalParticipants,     Icon: Users,    variant: 'emerald' },
    { title: 'Average Rating',     value: avgRating.toFixed(1),  Icon: Star,     variant: 'amber' },
  ];

  // Performance metrics from API
  const performanceMetrics = [
    { label: 'Total Sessions', value: stats.totalSessions || 0, color: 'text-green-600' },
    { label: 'Avg Engagement', value: `${stats.avgEngagement || 0}%`, color: 'text-blue-600' },
    { label: 'Completed', value: stats.completedSessions || 0, color: 'text-purple-600' },
    { label: 'Feedback Score', value: `${avgRating}/5`, color: 'text-amber-600' },
  ];

  // ── Getting-started checklist (branches solo vs org trainer) ───────────
  // A completed session implies activities were added and it was run live, so
  // those steps auto-check; "add activities" / "review report" also flip when
  // the user opens those pages from the checklist.
  const hasSession = (stats.totalSessions || 0) > 0;
  const hasRunLive = (stats.completedSessions || 0) > 0 || !!activeSession;
  const hasActivities = activitiesDone || (stats.completedSessions || 0) > 0;
  const hasReviewedReport = viewedReport || (stats.completedSessions || 0) > 0;

  const goToActivities = () => {
    localStorage.setItem('trainer_gs_activities', '1');
    setActivitiesDone(true);
    navigate('/trainer/sessions');
  };
  const goToReport = () => {
    localStorage.setItem('trainer_gs_viewed_report', '1');
    setViewedReport(true);
    navigate('/trainer/analytics');
  };

  const gettingStartedSteps = isSoloTrainer()
    ? [
        { key: 'session', label: 'Create a session', done: hasSession, cta: 'Create', action: () => navigate('/trainer/sessions/new') },
        { key: 'activities', label: 'Add sub-sessions & activities', done: hasActivities, cta: 'Set up', action: goToActivities },
        { key: 'live', label: 'Run a session live', done: hasRunLive, cta: 'Open sessions', action: () => navigate('/trainer/sessions') },
        { key: 'report', label: 'Review a session report', done: hasReviewedReport, cta: 'View analytics', action: goToReport },
      ]
    : [
        { key: 'session', label: 'Open your assigned session', done: hasSession, cta: 'Open sessions', action: () => navigate('/trainer/sessions') },
        { key: 'activities', label: 'Add activities to your session', done: hasActivities, cta: 'Set up', action: goToActivities },
        { key: 'live', label: 'Run a session live', done: hasRunLive, cta: 'Open sessions', action: () => navigate('/trainer/sessions') },
        { key: 'report', label: 'Review a session report', done: hasReviewedReport, cta: 'View analytics', action: goToReport },
      ];
  const gsCompleted = gettingStartedSteps.filter((s) => s.done).length;
  const gsAllDone = gsCompleted === gettingStartedSteps.length;

  // Once every step is complete, show the "all set" state briefly, then
  // auto-hide it permanently so it doesn't linger.
  useEffect(() => {
    if (loading || gsDismissed || !gsAllDone) return;
    const timer = setTimeout(() => {
      localStorage.setItem('trainer_gs_dismissed', '1');
      setGsDismissed(true);
    }, 3500);
    return () => clearTimeout(timer);
  }, [loading, gsDismissed, gsAllDone]);

  return (
    <DashboardLayout title="Trainer Dashboard">
      {/* Onboarding Tutorial Overlay */}
      {showOnboarding && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Blurred Background - dashboard visible behind */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="absolute inset-0 bg-black/40 backdrop-blur-md"
            onClick={handleCompleteOnboarding}
          />

          {/* Onboarding Card - Enhanced Modern Design */}
          <motion.div
            initial={{ opacity: 0, y: 50, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="relative z-10 w-full max-w-2xl"
          >
            <div className="bg-white rounded-2xl shadow-[0_25px_60px_-15px_rgba(0,0,0,0.3)] overflow-hidden">
              {/* Main Content Area */}
              <div className="flex flex-col md:flex-row min-h-[400px]">
                {/* Left Side - Enhanced Gradient Visual */}
                <div className={`md:w-[40%] p-6 flex flex-col items-center justify-center relative overflow-hidden ${
                  currentStep === 0 ? 'bg-gradient-to-br from-blue-500 via-blue-600 to-indigo-700' :
                  currentStep === 1 ? 'bg-gradient-to-br from-purple-500 via-purple-600 to-violet-700' :
                  currentStep === 2 ? 'bg-gradient-to-br from-emerald-500 via-emerald-600 to-teal-700' :
                  currentStep === 3 ? 'bg-gradient-to-br from-amber-500 via-orange-500 to-orange-600' :
                  'bg-gradient-to-br from-rose-500 via-rose-600 to-pink-700'
                }`}>
                  {/* Animated floating shapes */}
                  <motion.div
                    animate={{ y: [0, -8, 0], rotate: [0, 5, 0] }}
                    transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                    className="absolute top-6 right-6 w-12 h-12 bg-white/10 rounded-xl"
                  />
                  <motion.div
                    animate={{ y: [0, 8, 0], rotate: [0, -5, 0] }}
                    transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
                    className="absolute top-16 left-3 w-8 h-8 bg-white/10 rounded-full"
                  />
                  <motion.div
                    animate={{ scale: [1, 1.1, 1], opacity: [0.1, 0.2, 0.1] }}
                    transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                    className="absolute -bottom-8 -left-8 w-32 h-32 bg-white/10 rounded-full"
                  />

                  {/* Step Number */}
                  <motion.div
                    key={`step-num-${currentStep}`}
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="absolute top-4 left-4 w-8 h-8 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center"
                  >
                    <span className="text-white font-bold text-sm">{currentStep + 1}</span>
                  </motion.div>

                  {/* Icon with enhanced animation */}
                  <motion.div
                    key={`icon-${currentStep}`}
                    initial={{ scale: 0, rotate: -180, opacity: 0 }}
                    animate={{ scale: 1, rotate: 0, opacity: 1 }}
                    transition={{ type: "spring", damping: 15, stiffness: 200 }}
                    className="relative z-10"
                  >
                    {/* Pulsing ring effect */}
                    <motion.div
                      animate={{ scale: [1, 1.2, 1], opacity: [0.5, 0, 0.5] }}
                      transition={{ duration: 2, repeat: Infinity, ease: "easeOut" }}
                      className="absolute inset-0 w-20 h-20 bg-white/20 rounded-2xl"
                    />
                    <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center text-white shadow-2xl border border-white/30 relative">
                      <motion.div
                        animate={{ y: [0, -2, 0] }}
                        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                        className="w-10 h-10"
                      >
                        {onboardingSteps[currentStep].icon}
                      </motion.div>
                    </div>
                  </motion.div>

                  {/* Step Indicator Dots */}
                  <div className="flex items-center gap-1.5 mt-6">
                    {onboardingSteps.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentStep(index)}
                        className={`transition-all duration-300 ${
                          index === currentStep
                            ? 'w-6 h-2 bg-white rounded-full'
                            : index < currentStep
                            ? 'w-2 h-2 bg-white/60 rounded-full hover:bg-white/80'
                            : 'w-2 h-2 bg-white/30 rounded-full hover:bg-white/50'
                        }`}
                      />
                    ))}
                  </div>

                  {/* Progress Bar */}
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/20">
                    <motion.div
                      className="h-full bg-white"
                      initial={{ width: 0 }}
                      animate={{ width: `${((currentStep + 1) / onboardingSteps.length) * 100}%` }}
                      transition={{ duration: 0.4, ease: "easeOut" }}
                    />
                  </div>
                </div>

                {/* Right Side - Content */}
                <div className="md:w-[60%] p-6 flex flex-col relative">
                  {/* Close Button */}
                  <button
                    onClick={handleCompleteOnboarding}
                    className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full bg-neutral-100 hover:bg-neutral-200 text-neutral-400 hover:text-neutral-600 transition-all hover:rotate-90 duration-200"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>

                  {/* Welcome Badge - Only on first step */}
                  {currentStep === 0 && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.2 }}
                      className="inline-flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-primary-50 to-blue-50 text-primary-600 rounded-full text-xs font-semibold mb-4 w-fit border border-primary-100"
                    >
                      <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-primary-500"></span>
                      </span>
                      Welcome to Aktivity
                    </motion.div>
                  )}

                  {/* Content */}
                  <motion.div
                    key={currentStep}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.3, ease: "easeOut" }}
                    className="flex-1"
                  >
                    <h3 className="text-lg sm:text-xl font-bold text-neutral-900 mb-2 leading-tight">
                      {onboardingSteps[currentStep].title}
                    </h3>
                    <p className="text-neutral-500 leading-relaxed mb-4 text-sm">
                      {onboardingSteps[currentStep].description}
                    </p>

                    {/* Feature highlights with staggered animation */}
                    <div className="space-y-2 mb-4">
                      {currentStep === 0 && (
                        <>
                          <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 }} className="flex items-center gap-2 text-sm text-neutral-600 bg-neutral-50 rounded-lg px-3 py-2">
                            <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                              <svg className="w-3 h-3 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                            </div>
                            Create sub-sessions and topics
                          </motion.div>
                          <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }} className="flex items-center gap-2 text-sm text-neutral-600 bg-neutral-50 rounded-lg px-3 py-2">
                            <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                              <svg className="w-3 h-3 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                            </div>
                            Add interactive activities
                          </motion.div>
                        </>
                      )}
                      {currentStep === 1 && (
                        <>
                          <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 }} className="flex items-center gap-2 text-sm text-neutral-600 bg-neutral-50 rounded-lg px-3 py-2">
                            <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                              <svg className="w-3 h-3 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                            </div>
                            View pre-session challenges
                          </motion.div>
                          <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }} className="flex items-center gap-2 text-sm text-neutral-600 bg-neutral-50 rounded-lg px-3 py-2">
                            <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                              <svg className="w-3 h-3 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                            </div>
                            Tailor content based on responses
                          </motion.div>
                        </>
                      )}
                      {currentStep === 2 && (
                        <>
                          <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 }} className="flex items-center gap-2 text-sm text-neutral-600 bg-neutral-50 rounded-lg px-3 py-2">
                            <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                              <svg className="w-3 h-3 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                            </div>
                            Share session code with participants
                          </motion.div>
                          <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }} className="flex items-center gap-2 text-sm text-neutral-600 bg-neutral-50 rounded-lg px-3 py-2">
                            <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                              <svg className="w-3 h-3 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                            </div>
                            Launch activities during training
                          </motion.div>
                        </>
                      )}
                      {currentStep === 3 && (
                        <>
                          <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 }} className="flex items-center gap-2 text-sm text-neutral-600 bg-neutral-50 rounded-lg px-3 py-2">
                            <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                              <svg className="w-3 h-3 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                            </div>
                            Track real-time engagement
                          </motion.div>
                          <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }} className="flex items-center gap-2 text-sm text-neutral-600 bg-neutral-50 rounded-lg px-3 py-2">
                            <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                              <svg className="w-3 h-3 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                            </div>
                            View response rates live
                          </motion.div>
                        </>
                      )}
                      {currentStep === 4 && (
                        <>
                          <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 }} className="flex items-center gap-2 text-sm text-neutral-600 bg-neutral-50 rounded-lg px-3 py-2">
                            <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                              <svg className="w-3 h-3 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                            </div>
                            Review detailed session reports
                          </motion.div>
                          <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }} className="flex items-center gap-2 text-sm text-neutral-600 bg-neutral-50 rounded-lg px-3 py-2">
                            <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                              <svg className="w-3 h-3 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                            </div>
                            Analyze participant feedback
                          </motion.div>
                        </>
                      )}
                    </div>
                  </motion.div>

                  {/* Navigation */}
                  <div className="flex items-center justify-between pt-4 border-t border-neutral-100">
                    <button
                      onClick={handlePrevStep}
                      disabled={currentStep === 0}
                      className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                        currentStep === 0
                          ? 'text-neutral-300 cursor-not-allowed'
                          : 'text-neutral-600 hover:bg-neutral-100 hover:text-neutral-800'
                      }`}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                      Back
                    </button>

                    <div className="flex items-center gap-2">
                      <button
                        onClick={handleCompleteOnboarding}
                        className="px-3 py-2 text-sm font-medium text-neutral-400 hover:text-neutral-600 transition-colors"
                      >
                        Skip
                      </button>
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={handleNextStep}
                        className={`flex items-center gap-1.5 px-5 py-2.5 rounded-lg text-sm font-semibold text-white transition-all shadow-md ${
                          currentStep === 0 ? 'bg-gradient-to-r from-blue-500 to-indigo-600 shadow-blue-500/20' :
                          currentStep === 1 ? 'bg-gradient-to-r from-purple-500 to-violet-600 shadow-purple-500/20' :
                          currentStep === 2 ? 'bg-gradient-to-r from-emerald-500 to-teal-600 shadow-emerald-500/20' :
                          currentStep === 3 ? 'bg-gradient-to-r from-amber-500 to-orange-600 shadow-amber-500/20' :
                          'bg-gradient-to-r from-rose-500 to-pink-600 shadow-rose-500/20'
                        }`}
                      >
                        {currentStep === onboardingSteps.length - 1 ? (
                          <>
                            Get Started
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                            </svg>
                          </>
                        ) : (
                          <>
                            Continue
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                            </svg>
                          </>
                        )}
                      </motion.button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      <div className="p-6 lg:p-8 space-y-6 bg-neutral-50/50 dark:bg-neutral-950 min-h-screen pb-20 sm:pb-8">
        {/* Plan-expired banner — audience flips between solo-trainer (with CTA)
            and trainer (org member, no CTA — they can't pay). */}
        {planStatus?.expired && (
          <PlanExpiredBanner
            audience={isSoloTrainer() ? 'solo-trainer' : 'trainer'}
            planEnd={planStatus.planEnd}
          />
        )}

        {/* ────── Header ────── */}
        {/* Tight one-block header with status pill on the right. */}
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="space-y-1">
            <h1 className="text-2xl font-semibold tracking-tight text-neutral-900 dark:text-neutral-50">
              Good {new Date().getHours() < 12 ? 'morning' : 'afternoon'}, {user?.firstName || user?.fullName?.split(' ')[0] || 'Trainer'}!
            </h1>
            <p className="text-sm text-neutral-500 dark:text-neutral-400">Ready to inspire and educate today?</p>
            <p className="flex items-center gap-1.5 text-xs text-neutral-400 dark:text-neutral-500 mt-1">
              <Clock size={12} strokeWidth={1.75} />
              Last updated just now
            </p>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              type="button"
              onClick={handleReplayTour}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-neutral-600 dark:text-neutral-300 ring-1 ring-inset ring-neutral-200 dark:ring-neutral-700 hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors"
              title="Replay the welcome tour"
            >
              <Compass className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Show me around</span>
            </button>
            <div
              className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium ring-1 ring-inset whitespace-nowrap ${
                activeSession
                  ? 'bg-emerald-50 ring-emerald-200/60 text-emerald-700 dark:bg-emerald-500/10 dark:ring-emerald-500/30 dark:text-emerald-400'
                  : 'bg-neutral-100 ring-neutral-200 text-neutral-600 dark:bg-neutral-800 dark:ring-neutral-700 dark:text-neutral-400'
              }`}
            >
              <span
                aria-hidden="true"
                className={`w-2 h-2 rounded-full ${
                  activeSession
                    ? 'bg-emerald-500 animate-pulse motion-reduce:animate-none shadow-[0_0_0_3px_rgba(16,185,129,0.18)]'
                    : 'bg-neutral-400'
                }`}
              />
              {activeSession ? '1 live' : 'All quiet'}
            </div>
          </div>
        </div>

        {/* Active Session Alert */}
        {activeSession && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className={`${CARD_BASE} ring-emerald-200/60 dark:ring-emerald-500/20 p-4 sm:p-5`}
          >
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 bg-emerald-50 text-emerald-600 ring-1 ring-inset ring-emerald-100 dark:bg-emerald-500/10 dark:text-emerald-400 dark:ring-emerald-500/20 animate-pulse motion-reduce:animate-none">
                  <Video size={20} strokeWidth={1.75} />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 truncate">{activeSession.name}</h3>
                    <Badge variant="live" dot>
                      Live Now
                    </Badge>
                  </div>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
                    {activeSession.currentParticipants} participants • Code: {activeSession.code}
                  </p>
                </div>
              </div>
              <Button variant="primary" size="sm" onClick={() => navigate(`/trainer/session/${activeSession.id}/control`)}>
                Join Session
              </Button>
            </div>
          </motion.div>
        )}

        {/* Getting Started Checklist */}
        {!loading && !gsDismissed && (
          <div className={`${CARD_BASE} p-4 sm:p-5`}>
            <div className="flex items-start justify-between gap-3 mb-4">
              <div>
                <h2 className="text-base font-bold text-neutral-900 dark:text-neutral-50">
                  {gsAllDone ? "🎉 You're all set!" : 'Get started with Aktivity'}
                </h2>
                <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
                  {gsAllDone
                    ? "You've completed the key setup steps."
                    : `Complete these steps to run your first training · ${gsCompleted} of ${gettingStartedSteps.length} done`}
                </p>
              </div>
              <button
                type="button"
                onClick={dismissGettingStarted}
                className="text-xs font-medium text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200 transition-colors flex-shrink-0"
              >
                Dismiss
              </button>
            </div>

            {/* Progress bar */}
            <div className="h-1.5 w-full bg-neutral-100 dark:bg-neutral-800 rounded-full overflow-hidden mb-4">
              <div
                className="h-full bg-primary-500 transition-all duration-500"
                style={{ width: `${(gsCompleted / gettingStartedSteps.length) * 100}%` }}
              />
            </div>

            {/* Steps */}
            <ul className="space-y-2">
              {gettingStartedSteps.map((step) => (
                <li
                  key={step.key}
                  className={`flex items-center gap-3 rounded-lg px-3 py-2.5 ${
                    step.done
                      ? 'bg-neutral-50 dark:bg-neutral-800/50'
                      : 'bg-white dark:bg-neutral-900 ring-1 ring-inset ring-neutral-200/70 dark:ring-neutral-800'
                  }`}
                >
                  <span
                    className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                      step.done ? 'bg-green-500 text-white' : 'border-2 border-neutral-300 dark:border-neutral-600'
                    }`}
                  >
                    {step.done && (
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </span>
                  <span
                    className={`flex-1 text-sm ${
                      step.done
                        ? 'text-neutral-400 dark:text-neutral-500 line-through'
                        : 'text-neutral-700 dark:text-neutral-200 font-medium'
                    }`}
                  >
                    {step.label}
                  </span>
                  {!step.done && (
                    <button
                      type="button"
                      onClick={step.action}
                      className="text-xs font-semibold text-primary-600 hover:text-primary-700 dark:text-primary-400 px-3 py-1.5 rounded-lg hover:bg-primary-50 dark:hover:bg-primary-500/10 transition-colors whitespace-nowrap"
                    >
                      {step.cta} →
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* ────── Row A: Stats ────── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5">
          {statsCards.map((stat) => (
            <StatCard key={stat.title} label={stat.title} value={stat.value} icon={stat.Icon} variant={stat.variant} />
          ))}
        </div>

        {/* Upcoming Sessions */}
        <div className={`${CARD_BASE} overflow-hidden`}>
          <div className="p-5 sm:p-6 border-b border-neutral-100 dark:border-neutral-800/70">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-start gap-3">
                <span className={SECTION_HEADER_CHIP}>
                  <CalendarClock size={16} strokeWidth={1.75} />
                </span>
                <div>
                  <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-50 leading-tight">Upcoming Sessions</h2>
                  <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5">Your scheduled training sessions</p>
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => navigate('/trainer/sessions')}>
                View All
              </Button>
            </div>
          </div>

          {/* Desktop Table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-neutral-50/70 dark:bg-neutral-900/60">
                  <th className="text-left py-3 px-5 text-[11px] font-semibold uppercase tracking-[0.06em] text-neutral-500 dark:text-neutral-400">Session</th>
                  <th className="text-left py-3 px-5 text-[11px] font-semibold uppercase tracking-[0.06em] text-neutral-500 dark:text-neutral-400">Date &amp; Time</th>
                  <th className="text-left py-3 px-5 text-[11px] font-semibold uppercase tracking-[0.06em] text-neutral-500 dark:text-neutral-400">Participants</th>
                  <th className="text-left py-3 px-5 text-[11px] font-semibold uppercase tracking-[0.06em] text-neutral-500 dark:text-neutral-400">Status</th>
                  <th className="text-right py-3 px-5 text-[11px] font-semibold uppercase tracking-[0.06em] text-neutral-500 dark:text-neutral-400">Actions</th>
                </tr>
              </thead>
              <tbody>
                {upcomingSessionsList.map((session, index, arr) => {
                  const isLast = index === arr.length - 1;
                  return (
                    <tr
                      key={session.id}
                      className={`hover:bg-neutral-50 dark:hover:bg-neutral-900/50 transition-colors cursor-pointer ${
                        isLast ? '' : 'border-b border-neutral-100 dark:border-neutral-800/70'
                      }`}
                      onClick={() => navigate(`/trainer/sessions/${session.id}`)}
                    >
                      <td className="py-3 px-5 align-top">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0 ring-1 ring-inset bg-gradient-to-br from-primary-100 to-primary-200 text-primary-700 ring-primary-200/60 dark:from-primary-500/20 dark:to-primary-700/20 dark:text-primary-300 dark:ring-primary-500/30">
                            {session.name.charAt(0).toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 truncate">{session.name}</p>
                            <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5 font-mono">Code: {session.code}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-5 align-top">
                        <p className="text-sm text-neutral-700 dark:text-neutral-300 tabular-nums">
                          {session.scheduledDate ? formatDateWithTimezone(session.scheduledDate) : ''} {session.startTime ? formatTime(session.startTime) : ''}
                        </p>
                      </td>
                      <td className="py-3 px-5 align-top">
                        <span className="text-sm tabular-nums text-neutral-700 dark:text-neutral-300">
                          {session.totalParticipants ?? session.participants?.length ?? 0}/{session.expectedParticipants || session.maxParticipants || 0}
                        </span>
                      </td>
                      <td className="py-3 px-5 align-top">
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ring-1 ring-inset bg-blue-50 text-blue-700 ring-blue-200 dark:bg-blue-500/10 dark:text-blue-300 dark:ring-blue-500/30">
                          {session.status}
                        </span>
                      </td>
                      <td className="py-3 px-5 align-top text-right" onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          onClick={() => navigate(`/trainer/sessions/${session.id}`)}
                          className="p-1.5 rounded-lg text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
                          title="View"
                        >
                          <Eye size={16} strokeWidth={1.75} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden">
            {upcomingSessionsList.map((session) => (
              <div
                key={session.id}
                className="p-4 border-b border-neutral-100 dark:border-neutral-800/70 last:border-0 active:bg-neutral-50 dark:active:bg-neutral-900/50 cursor-pointer"
                onClick={() => navigate(`/trainer/sessions/${session.id}`)}
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0 ring-1 ring-inset bg-gradient-to-br from-primary-100 to-primary-200 text-primary-700 ring-primary-200/60 dark:from-primary-500/20 dark:to-primary-700/20 dark:text-primary-300 dark:ring-primary-500/30">
                    {session.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 truncate">{session.name}</p>
                    <p className="text-xs text-neutral-500 dark:text-neutral-400 font-mono">Code: {session.code}</p>
                  </div>
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ring-1 ring-inset bg-blue-50 text-blue-700 ring-blue-200 dark:bg-blue-500/10 dark:text-blue-300 dark:ring-blue-500/30">
                    {session.status}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-xs text-neutral-500 dark:text-neutral-400 ml-12 flex-wrap">
                  <span className="tabular-nums">
                    {session.scheduledDate ? formatDateWithTimezone(session.scheduledDate) : ''} {session.startTime ? formatTime(session.startTime) : ''}
                  </span>
                  <span className="text-neutral-300 dark:text-neutral-700">&middot;</span>
                  <span className="tabular-nums">{session.totalParticipants ?? session.participants?.length ?? 0}/{session.expectedParticipants || session.maxParticipants || 0}</span>
                </div>
              </div>
            ))}
          </div>

          {upcomingSessionsList.length === 0 && (
            <div className="text-center py-12 md:py-16 px-4">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-neutral-100 dark:bg-neutral-800 ring-1 ring-inset ring-neutral-200/70 dark:ring-neutral-800 shadow-[inset_0_-2px_4px_rgba(0,0,0,0.04)]">
                <Calendar size={24} strokeWidth={1.75} className="text-neutral-400" />
              </div>
              <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 mt-4">No upcoming sessions</p>
              <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">Sessions will appear here when assigned</p>
            </div>
          )}

          <div className="px-5 sm:px-6 py-4 border-t border-neutral-100 dark:border-neutral-800/70">
            <Button variant="primary" size="sm" fullWidth onClick={() => navigate('/trainer/sessions')}>
              View All Sessions
            </Button>
          </div>
        </div>

        {/* Performance Indices with Custom Bar Chart - Full Width */}
        {(() => {
          const completedCount = stats.completedSessions || 0;
          const scheduledCount = stats.upcomingSessionsCount || 0;
          const liveCount = stats.liveSessions || 0;
          const totalCount = stats.totalSessions || 0;
          const expiredCount = Math.max(0, totalCount - completedCount - scheduledCount - liveCount);

          const barData = [
            { label: 'Completed', shortLabel: 'Completed', value: completedCount, displayValue: `${completedCount}`,
              gradient: 'linear-gradient(180deg, #6EE7B7 0%, #22C55E 100%)', light: '#DCFCE7', shadow: 'rgba(34,197,94,0.3)', text: 'text-green-700' },
            { label: 'Expired', shortLabel: 'Expired', value: expiredCount, displayValue: `${expiredCount}`,
              gradient: 'linear-gradient(180deg, #FCA5A5 0%, #EF4444 100%)', light: '#FEE2E2', shadow: 'rgba(239,68,68,0.3)', text: 'text-red-700' },
            { label: 'Scheduled', shortLabel: 'Scheduled', value: scheduledCount, displayValue: `${scheduledCount}`,
              gradient: 'linear-gradient(180deg, #93C5FD 0%, #3B82F6 100%)', light: '#DBEAFE', shadow: 'rgba(59,130,246,0.3)', text: 'text-blue-700' },
          ];
          const maxVal = Math.max(...barData.map(d => d.value), 1);
          const maxIndex = barData.findIndex(d => d.value === maxVal);

          return (
            <div className={`${CARD_BASE} p-5 sm:p-6`}>
              {/* Header */}
              <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
                <div className="flex items-start gap-3">
                  <span className={SECTION_HEADER_CHIP}>
                    <BarChart3 size={16} strokeWidth={1.75} />
                  </span>
                  <div>
                    <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-50 leading-tight">Performance Indices</h2>
                    <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5">Session breakdown</p>
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => navigate('/trainer/analytics')}>
                  View Details
                </Button>
              </div>

              {/* Summary Value */}
              <div className="mb-1">
                <span className="text-3xl font-semibold tabular-nums tracking-tight text-neutral-900 dark:text-neutral-50">{totalCount}</span>
                <span className="text-sm text-neutral-500 dark:text-neutral-400 ml-2">total sessions</span>
              </div>

              {/* Growth Badge */}
              <div className="flex items-center gap-2 mb-8">
                <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ring-1 ring-inset bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20">
                  <TrendingUp size={11} strokeWidth={2} />
                  {stats.avgEngagement || 0}%
                </span>
                <span className="text-xs text-neutral-400 dark:text-neutral-500">avg engagement</span>
              </div>

              {/* Chart + Metrics Side by Side */}
              <div className="flex flex-col lg:flex-row gap-6">
                {/* Custom Bars */}
                <div className="flex-1 flex items-end justify-between gap-4 sm:gap-8" style={{ height: 200 }}>
                  {barData.map((item, index) => {
                    const heightPercent = maxVal > 0 ? (item.value / maxVal) * 100 : 0;
                    const barHeight = Math.max(heightPercent, 6);
                    const isHighest = index === maxIndex && item.value > 0;

                    return (
                      <div key={item.label} className="flex-1 flex flex-col items-center gap-2">
                        {/* Value label above bar */}
                        <div className={`text-xs font-bold ${item.text}`}>
                          {item.displayValue}
                        </div>

                        {/* Bar */}
                        <div className="w-full flex justify-center" style={{ height: 150 }}>
                          <motion.div
                            initial={{ height: 0 }}
                            animate={{ height: `${barHeight}%` }}
                            transition={{ duration: 0.8, delay: index * 0.1, ease: [0.34, 1.56, 0.64, 1] }}
                            className="w-full max-w-[56px] rounded-xl self-end"
                            style={{
                              background: isHighest ? item.gradient : item.light,
                              boxShadow: isHighest ? `0 4px 14px -2px ${item.shadow}` : 'none',
                            }}
                          />
                        </div>

                        {/* Label */}
                        <span className={`text-xs font-medium ${isHighest ? 'text-neutral-900' : 'text-neutral-400'}`}>
                          {item.shortLabel}
                        </span>
                      </div>
                    );
                  })}
                </div>

                {/* Metrics List */}
                <div className="lg:w-64 flex-shrink-0 flex flex-col justify-center">
                  {performanceMetrics.map((metric, idx) => (
                    <div
                      key={metric.label}
                      className={`flex items-center justify-between py-3 px-1 ${idx !== performanceMetrics.length - 1 ? 'border-b border-neutral-100 dark:border-neutral-800/70' : ''}`}
                    >
                      <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">{metric.label}</span>
                      <span className={`text-sm font-semibold tabular-nums ${metric.color}`}>{metric.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          );
        })()}

        {/* Recent Activity Timeline */}
        <div className={`${CARD_BASE} p-5 sm:p-6`}>
          <div className="flex items-start justify-between mb-5">
            <div className="flex items-start gap-3">
              <span className={SECTION_HEADER_CHIP}>
                <Activity size={16} strokeWidth={1.75} />
              </span>
              <div>
                <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-50 leading-tight">Recent Activity</h2>
                <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5">Recent events and updates</p>
              </div>
            </div>
          </div>

          {recentActivity.length > 0 ? (
            <div className="relative">
              {recentActivity.slice(0, 6).map((activity, index) => {
                const isLast = index === Math.min(recentActivity.length, 6) - 1;
                const getActivityInfo = (type) => {
                  switch(type) {
                    case 'session_completed':
                      return {
                        cls: 'bg-emerald-50 text-emerald-600 ring-1 ring-inset ring-emerald-100 dark:bg-emerald-500/10 dark:text-emerald-400 dark:ring-emerald-500/20',
                        Icon: CheckCircle2,
                        action: 'Session completed',
                        category: 'Sessions',
                      };
                    case 'session_live':
                      return {
                        cls: 'bg-rose-50 text-rose-600 ring-1 ring-inset ring-rose-100 dark:bg-rose-500/10 dark:text-rose-400 dark:ring-rose-500/20',
                        Icon: Radio,
                        action: 'Session went live',
                        category: 'Sessions',
                      };
                    case 'session_scheduled':
                      return {
                        cls: 'bg-blue-50 text-blue-600 ring-1 ring-inset ring-blue-100 dark:bg-blue-500/10 dark:text-blue-400 dark:ring-blue-500/20',
                        Icon: Calendar,
                        action: 'Session scheduled',
                        category: 'Sessions',
                      };
                    case 'feedback_received':
                      return {
                        cls: 'bg-amber-50 text-amber-600 ring-1 ring-inset ring-amber-100 dark:bg-amber-500/10 dark:text-amber-400 dark:ring-amber-500/20',
                        Icon: Star,
                        action: 'Feedback received',
                        category: 'Feedback',
                      };
                    default:
                      return {
                        cls: 'bg-blue-50 text-blue-600 ring-1 ring-inset ring-blue-100 dark:bg-blue-500/10 dark:text-blue-400 dark:ring-blue-500/20',
                        Icon: Calendar,
                        action: 'Activity',
                        category: 'General',
                      };
                  }
                };
                const activityInfo = getActivityInfo(activity.type);
                const ActIcon = activityInfo.Icon;
                const timeAgo = activity.timestamp ? (() => {
                  const date = new Date(activity.timestamp);
                  const now = new Date();
                  const diffMs = now - date;
                  const diffMins = Math.floor(diffMs / 60000);
                  const diffHours = Math.floor(diffMs / 3600000);
                  const diffDays = Math.floor(diffMs / 86400000);
                  if (diffMins < 60) return `${diffMins} minutes ago`;
                  if (diffHours < 24) return `${diffHours} hours ago`;
                  if (diffDays < 7) return `${diffDays} days ago`;
                  return `${Math.floor(diffDays / 7)} weeks ago`;
                })() : '';

                return (
                  <div key={index} className="relative flex gap-4 pb-6 last:pb-0">
                    {!isLast && (
                      <div className="absolute left-5 top-10 bottom-0 w-px bg-gradient-to-b from-neutral-200 to-transparent dark:from-neutral-800" />
                    )}
                    <div className={`relative z-10 w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${activityInfo.cls}`}>
                      <ActIcon size={16} strokeWidth={1.75} />
                    </div>
                    <div className="flex-1 min-w-0 pt-1">
                      <p className="text-sm text-neutral-900 dark:text-neutral-100 font-medium">{activityInfo.action}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-neutral-400 dark:text-neutral-500">{timeAgo}</span>
                        {activityInfo.category && (
                          <>
                            <span className="text-neutral-300 dark:text-neutral-700">&middot;</span>
                            <span className="text-xs font-medium text-neutral-500 dark:text-neutral-400">{activityInfo.category}</span>
                          </>
                        )}
                      </div>
                      {(activity.title || activity.description) && (
                        <div className="mt-2 px-3 py-2 rounded-lg bg-neutral-50/70 dark:bg-neutral-900/60 ring-1 ring-inset ring-neutral-200/60 dark:ring-neutral-800">
                          <p className="text-xs text-neutral-600 dark:text-neutral-400 leading-relaxed line-clamp-2">
                            {activity.title}{activity.description ? ` — ${activity.description}` : ''}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-neutral-400 dark:text-neutral-500 text-sm">No recent activity</div>
          )}

          <div className="mt-4 pt-4 border-t border-neutral-100 dark:border-neutral-800/70">
            <Button variant="ghost" size="sm" fullWidth onClick={() => navigate('/trainer/analytics')}>
              View All Activity
            </Button>
          </div>
        </div>

        {/* Quick Actions */}
        <div className={`${CARD_BASE} p-5 sm:p-6`}>
          <div className="flex items-start gap-3 mb-4">
            <span className={SECTION_HEADER_CHIP}>
              <Plus size={16} strokeWidth={1.75} />
            </span>
            <div>
              <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-50 leading-tight">Quick Actions</h2>
              <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5">Common trainer operations</p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <button
              type="button"
              onClick={() => navigate('/trainer/sessions')}
              className="group rounded-2xl bg-white dark:bg-neutral-900 ring-1 ring-inset ring-neutral-200/70 dark:ring-neutral-800 p-4 text-left hover:-translate-y-0.5 hover:shadow-[0_4px_12px_-4px_rgba(16,24,40,0.10)] transition-all duration-200"
            >
              <div className="flex items-center gap-3 sm:flex-col sm:items-start">
                <span className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform ${STAT_CHIP.blue}`}>
                  <Video size={20} strokeWidth={1.75} />
                </span>
                <div className="sm:mt-3">
                  <h3 className="font-semibold text-neutral-900 dark:text-neutral-100 text-sm group-hover:text-primary-700 dark:group-hover:text-primary-300 transition-colors">My Sessions</h3>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">View assigned sessions</p>
                </div>
              </div>
            </button>

            <button
              type="button"
              onClick={() => navigate('/trainer/analytics')}
              className="group rounded-2xl bg-white dark:bg-neutral-900 ring-1 ring-inset ring-neutral-200/70 dark:ring-neutral-800 p-4 text-left hover:-translate-y-0.5 hover:shadow-[0_4px_12px_-4px_rgba(16,24,40,0.10)] transition-all duration-200"
            >
              <div className="flex items-center gap-3 sm:flex-col sm:items-start">
                <span className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform ${STAT_CHIP.violet}`}>
                  <BarChart3 size={20} strokeWidth={1.75} />
                </span>
                <div className="sm:mt-3">
                  <h3 className="font-semibold text-neutral-900 dark:text-neutral-100 text-sm group-hover:text-primary-700 dark:group-hover:text-primary-300 transition-colors">Performance</h3>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">View your analytics</p>
                </div>
              </div>
            </button>

            <button
              type="button"
              onClick={() => navigate('/trainer/schedule')}
              className="group rounded-2xl bg-white dark:bg-neutral-900 ring-1 ring-inset ring-neutral-200/70 dark:ring-neutral-800 p-4 text-left hover:-translate-y-0.5 hover:shadow-[0_4px_12px_-4px_rgba(16,24,40,0.10)] transition-all duration-200"
            >
              <div className="flex items-center gap-3 sm:flex-col sm:items-start">
                <span className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform ${STAT_CHIP.emerald}`}>
                  <Calendar size={20} strokeWidth={1.75} />
                </span>
                <div className="sm:mt-3">
                  <h3 className="font-semibold text-neutral-900 dark:text-neutral-100 text-sm group-hover:text-primary-700 dark:group-hover:text-primary-300 transition-colors">View Schedule</h3>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">Check your calendar</p>
                </div>
              </div>
            </button>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default TrainerDashboard;
